import React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { portnetLogoBlack } from '../../../../asset/images/LandingPage';
import { PTTranslate } from '../../../../common/components/ui';

// Footer Link Component
const FooterLink = ({ href, children }) => {
  const theme = useTheme();
  
  return (
    <Typography 
      component="a" 
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ 
        color: '#777', 
        textDecoration: 'none', 
        fontSize: '0.75rem', 
        '&:hover': { color: '#555' }
      }}
    >
      {children}
    </Typography>
  );
};

const SocialIcon = ({ Icon, href }) => {
  return (
    <Typography 
      component="a" 
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ color: '#777', '&:hover': { color: '#555' } }}
    >
      <Icon fontSize="small" />
    </Typography>
  );
};

const ColumnHeader = ({ children }) => {
  return (
    <Typography 
      variant="subtitle1" 
      sx={{ 
        color: '#333', 
        mb: 2, 
        fontWeight: 600, 
        fontSize: '0.9rem' 
      }}
    >
      {children}
    </Typography>
  );
};

const ContactInfoSection = () => {
  const theme = useTheme();
  
  return (
    <Grid item xs={12} sm={6} md={3} sx={{ 
      textAlign: { xs: 'center', md: 'left' },
      display: 'flex',
      flexDirection: 'column',
      alignItems: { xs: 'center', md: 'flex-start' }
    }}>
      <Box sx={{
        width: '100%',
        display: 'flex',
        justifyContent: { xs: 'center', md: 'flex-start' },
        mb: { xs: 2, md: 0 }
      }}>
        <Box
          component="img"
          src={portnetLogoBlack}
          alt="PORTNET"
          sx={{
            mt: { xs: 0, md: -7 },
            mb: { xs: 2, md: -3 },
            width: { xs: '150px', md: 'auto' },
            maxWidth: '100%',
            objectFit: 'contain'
          }}
        />
      </Box>
      <Typography variant="body2" sx={{ 
        color: '#555', 
        fontSize: '0.9rem', 
        mb: 1, 
        lineHeight: 1.7,
        maxWidth: { xs: '80%', sm: '100%' }
      }}>
        <PTTranslate tk="footer.address" />
      </Typography>
      <Typography variant="body2" sx={{ color: '#555', fontSize: '0.9rem', mb: 0.5 }}>
        <PTTranslate tk="footer.phone" />
      </Typography>
      <Typography variant="body2" sx={{ color: '#555', fontSize: '0.9rem', mb: 0.5 }}>
        <PTTranslate tk="footer.fax" />
      </Typography>
      <Typography 
        variant="body2" 
        component="a" 
        href="mailto:contact@portnet.ma"
        target="_blank"
        rel="noopener noreferrer"
        sx={{ 
          color: '#555',
          textDecoration: 'none',
          display: 'block',
          fontSize: '0.9rem',
          mb: 3,
          '&:hover': { color: theme.palette.primary.main }
        }}
      >
        <PTTranslate tk="footer.email" />
      </Typography>
    </Grid>
  );
};

// About Section Component
const AboutSection = () => {
  const theme = useTheme();
  
  return (
    <Grid item xs={12} sm={6} md={3} lg={2} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
      <Typography variant="subtitle1" sx={{ color: '#333', mb: 3, fontWeight: 700, fontSize: '1rem', letterSpacing: '0.5px' }}>
        <PTTranslate tk="footer.about" />
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography 
          component="a" 
          href="https://www.portnet.ma/qui-sommes-nous-" 
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: '#666', textDecoration: 'none', fontSize: '0.9rem', '&:hover': { color: theme.palette.primary.main } }}
        >
          <PTTranslate tk="footer.whoWeAre" />
        </Typography>
        <Typography 
          component="a" 
          href="https://www.portnet.ma/blog-actualites-portnet?_com_liferay_blogs_web_portlet_BlogsAgreggatorPortlet_delta=20&p_p_id=com_liferay_blogs_web_portlet_BlogsAgreggatorPortlet" 
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: '#666', textDecoration: 'none', fontSize: '0.9rem', '&:hover': { color: theme.palette.primary.main } }}
        >
          <PTTranslate tk="footer.news" />
        </Typography>
        <Typography variant="body2" sx={{ color: '#333', fontSize: '1rem', mt: 1, fontWeight: 700 }}>
          <PTTranslate tk="footer.followUs" />
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: { xs: 'center', md: 'flex-start' } }}>
          <SocialIcon Icon={FacebookIcon} href="https://www.facebook.com/PORTNETSA?_rdc=1&_rdr" />
          <SocialIcon Icon={TwitterIcon} href="https://twitter.com/PORTNETSA" />
          <SocialIcon Icon={InstagramIcon} href="https://www.instagram.com/portnet_officiel/" />
          <SocialIcon Icon={LinkedInIcon} href="https://www.linkedin.com/company/portnet/" />
        </Box>
      </Box>
    </Grid>
  );
};

// Links Section Component with configurable links
const LinksSection = ({ title, links, translationKeys }) => {
  return (
    <Grid item xs={12} sm={6} md={2} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
      <ColumnHeader><PTTranslate tk={title} /></ColumnHeader>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {links.map((link, index) => (
          <FooterLink key={index} href={link.href}>
            <PTTranslate tk={translationKeys[index]} />
          </FooterLink>
        ))}
      </Box>
    </Grid>
  );
};

// Footer Bottom Component
const FooterBottom = () => {
  return (
    <Box sx={{ 
      mt: 4,
      pt: 2,
      pb: 2,
      border: '1px solid #17203D',
      borderRadius: 5,
      backgroundColor: '#17203D',
      width: '100%',
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: { xs: 2, md: 0 },
      px: { xs: 2, sm: 4 },
    }}>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
        {[
          { text: <PTTranslate tk="footer.cookiesPolicy" />, href: "#" },
          { text: <PTTranslate tk="footer.termsAndConditions" />, href: "#" },
          { text: <PTTranslate tk="footer.privacyPolicy" />, href: "#" }
        ].map((item, index) => (
          <Typography 
            key={index}
            component="a" 
            href={item.href} 
            target="_blank"
            rel="noopener noreferrer"
            sx={{ 
              color: '#fff', 
              textDecoration: 'none', 
              fontSize: '0.875rem',
              fontWeight: '600',
              transition: 'color 0.3s ease-in-out', 
              '&:hover': { color: '#d3d3d3' }
            }}
          >
            {item.text}
          </Typography>
        ))}
      </Box>
      <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.875rem', fontWeight: '600', mt: { xs: 2, md: 0 } }}>
        <PTTranslate tk="footer.copyright" />
      </Typography>
    </Box>
  );
};

// Main Footer Component
const Footer = () => {
  // PortNet Links with proper URLs
  const portnetLinks = [
    { text: 'Accueil', href: 'https://www.portnet.ma/' },
    { text: 'Nos services', href: 'https://www.portnet.ma/nos-services' },
    { text: 'Actualités', href: 'https://www.portnet.ma/blog-actualites-portnet?_com_liferay_blogs_web_portlet_BlogsAgreggatorPortlet_delta=20&p_p_id=com_liferay_blogs_web_portlet_BlogsAgreggatorPortlet' },
    { text: 'Formation', href: 'https://www.portnet.ma/formation' },
    { text: 'Assistance', href: 'https://www.portnet.ma/support' }
  ];
  
  // Translation keys for PortNet links
  const portnetLinkKeys = [
    'footer.home',
    'footer.ourServices',
    'footer.updates',
    'footer.training',
    'footer.assistance'
  ];
  
  // Portails Links with proper URLs
  const portailsLinks = [
    { text: 'Portail Institutionel', href: 'https://www.portnet.ma/' },
    { text: 'Guichet Unique', href: 'https://portail.portnet.ma/' },
    { text: 'TradeSense', href: 'https://tradesense.ma/home' },
    { text: 'Portail Innovation', href: 'https://innovation.portnet.ma/' }
  ];
  
  // Translation keys for Portails links
  const portailsLinkKeys = [
    'footer.institutionalPortal',
    'footer.singleWindow',
    'footer.tradeSense',
    'footer.innovationPortal'
  ];
  
  // Translation keys for Company links
  const companyLinkKeys = [
    'footer.aboutUs',
    'footer.ourUpdates',
    'footer.seoChecker',
    'footer.tools',
    'footer.marketingStudies',
    'footer.careers'
  ];
  
  return (
    <Box sx={{ py: { xs: 6, md: 11 }, backgroundColor: '#fff', width: '100%' }}>
      <Container maxWidth={false} sx={{ px: { xs: 2, md: 6, lg: 10 } }}>
        <Grid 
          container 
          spacing={4} 
          justifyContent="center" 
          sx={{ mb: { xs: 4, md: 9 } }}
        >
          <ContactInfoSection />
          <AboutSection />
          <LinksSection 
            title="footer.portnet" 
            links={portnetLinks} 
            translationKeys={portnetLinkKeys} 
          />
          <LinksSection 
            title="footer.portals" 
            links={portailsLinks} 
            translationKeys={portailsLinkKeys} 
          />
        </Grid>
        <FooterBottom />
      </Container>
    </Box>
  );
};

export default Footer;